export { createProductIcon } from './components/product-icon.js';
import { registerPositiveNav } from './components/positive-nav-element.js';
import { resolveCatalog } from './core/catalog.js';
import { normalizeOptions } from './core/options.js';
import { notify, reportError } from './core/callbacks.js';
import { supportsNavigation } from './core/support.js';
import { createTrackedUrl } from './core/tracking.js';
import { createTargetMarker } from './core/target-marker.js';

function resolveTarget(target, document) {
  let container;
  try {
    container = target === undefined ? document.body: typeof target === 'string'
      ? document.querySelector(target): target;
  } catch {
    throw new Error(`[PositiveNav] Invalid target selector: ${target}`);
  }
  if (!container || container.nodeType !== 1 || container.namespaceURI !== 'http://www.w3.org/1999/xhtml') {
    throw new Error(`[PositiveNav] HTML target not found: ${target ?? 'document.body'}`);
  }
  return container;
}

/**
 * @param {{ target?: string | HTMLElement, locale?: string, theme?: 'light' | 'dark', currentProduct?: string, connectedApps?: string[], hiddenProducts?: string[], agentOnboardingUrl?: string | null, style?: { position?: 'fixed' | 'sticky' | 'relative', zIndex?: number }, callbacks?: object }} [options={}]
 * @returns {object|null} The controller, or null when required browser APIs are unavailable.
 */
export function init(options = {}) {
  let element;
  let state;
  let catalog;
  let destroyed = false;
  let api;
  let targetMarker;

  try {
    if (typeof document === 'undefined') return null;
    // Validate options before reading target; the locale depends on the target document.
    state = normalizeOptions(options, undefined, document.documentElement.lang);
    const container = resolveTarget(state.target, document);
    const owner = container.ownerDocument;
    if (!supportsNavigation(owner.defaultView)) return null;
    if (owner !== document) state = normalizeOptions(options, undefined, owner.documentElement.lang);
    catalog = resolveCatalog(state, owner.location?.hostname ?? '');
    registerPositiveNav(owner.defaultView);
    element = owner.createElement('positive-nav');
    element.id = element.instanceId;
    element.className = 'positive-nav-root';
    element.render(state, catalog);
    targetMarker = createTargetMarker();

    function update(partial = {}) {
      try {
        if (destroyed) throw new Error('[PositiveNav] This instance has been destroyed.');
        const previous = state;
        const next = normalizeOptions(partial, state, element.ownerDocument.documentElement.lang);
        const target = resolveTarget(next.target, element.ownerDocument);
        if (target === element || element.contains(target) || target.getRootNode().host === element) {
          throw new Error('[PositiveNav] target cannot be PositiveNav or one of its descendants.');
        }
        const nextCatalog = resolveCatalog(next, target.ownerDocument.location?.hostname ?? '');
        // Close callbacks triggered during rendering must see the new state.
        state = next;
        catalog = nextCatalog;
        if (element.parentElement !== target || next.target !== previous.target) {
          if (next.target === undefined) target.prepend(element);
          else target.append(element);
        }
        if (destroyed || state !== next) return api;
        targetMarker.update(target, next.style.position);
        if (destroyed || state !== next) return api;
        element.render(next, nextCatalog);
        if (destroyed || state !== next) return api;
        if (previous.theme !== next.theme) {
          notify(next.callbacks, 'onThemeChange', { theme: next.theme, previousTheme: previous.theme });
        }
        if (!destroyed && state === next && previous.locale !== next.locale) {
          notify(next.callbacks, 'onLocaleChange', { locale: next.locale, previousLocale: previous.locale });
        }
        return api;
      } catch (error) {
        const handler = typeof partial?.callbacks?.onError === 'function'
          ? partial.callbacks: state.callbacks;
        reportError(handler, error);
        throw error;
      }
    }

    api = Object.freeze({
      element,
      get catalog() { return catalog; },
      get options() { return state; },
      update,
      setLocale(locale) { return update({ locale }); },
      setTheme(theme) { return update({ theme }); },
      getHeight() { return destroyed ? 0: element.getBarHeight(); },
      destroy() {
        if (destroyed) return;
        destroyed = true;
        element.dispose();
        element.remove();
        targetMarker.dispose();
        state = Object.freeze({ ...state, callbacks: Object.freeze({}) });
      },
    });

    element.setPanelHandler((name, payload) => notify(state.callbacks, name, Object.freeze(payload)));
    element.setTrackHandler(
      (payload) => { if (!destroyed) notify(state.callbacks, 'onTrack', Object.freeze(payload)); },
      (error) => { if (!destroyed) reportError(state.callbacks, error); },
    );
    element.setActionHandler((action, originalEvent) => {
      const external = action.action === 'navigate' || action.action === 'connectAgent';
      if (destroyed || !element.isConnected) {
        if (external) originalEvent.preventDefault();
        return;
      }
      const link = action.action === 'navigate'
        ? originalEvent.composedPath().find((node) => node?.localName === 'a'): undefined;
      const trackedAction = action.action === 'navigate'
        ? { ...action, url: createTrackedUrl(action, catalog.currentProduct?.id) }: action;
      const result = notify(state.callbacks, 'onAction', Object.freeze({ ...trackedAction, originalEvent }));
      if (result === false || originalEvent.defaultPrevented || destroyed || !element.isConnected) {
        if (external) originalEvent.preventDefault();
        return;
      }
      if (action.action === 'openCatalog' || action.action === 'openProduct') {
        if (action.action === 'openProduct' && action.source === 'product'
          && catalog.visibleProducts.find(({ id }) => id === action.productId)?.isConnected) return;
        try { element.openPanel(action, originalEvent); } catch (error) { reportError(state.callbacks, error); }
        return;
      }
      if (action.action === 'close') { element.closePanel(action); return; }
      if (['openAgentOnboarding', 'closeAgentOnboarding', 'copyAgentPrompt', 'connectAgent'].includes(action.action)) {
        try {
          const allowed = element.performAgentAction(action, originalEvent);
          if (external && !allowed) originalEvent.preventDefault();
        } catch (error) {
          if (external) originalEvent.preventDefault();
          reportError(state.callbacks, error);
        }
        return;
      }
      if (action.action !== 'navigate') return;
      // The callback can change access or filters before navigation.
      const product = catalog.visibleProducts.find(({ id, isCurrent }) => id === action.productId && !isCurrent);
      if (!product) { originalEvent.preventDefault(); return; }
      const destination = action.source === 'product' ? 'app': action.destination;
      if (!['app', 'try', 'discover'].includes(destination)
        || (destination === 'app' ? !product.isConnected: product.isConnected)
        || action.url !== product.urls[destination]
        || trackedAction.url !== createTrackedUrl(action, catalog.currentProduct?.id)
        || !link?.isConnected || link.href !== trackedAction.url) {
        originalEvent.preventDefault();
        return;
      }
      // Leave valid links to the browser, including modified and middle clicks.
      try {
        if (action.source === 'catalog' || action.source === 'productPanel') element.closePanel({ reason: 'navigate' });
      } catch (error) {
        reportError(state.callbacks, error);
      }
    });

    if (state.target === undefined) container.prepend(element);
    else container.append(element);
    targetMarker.update(container, state.style.position);
  } catch (error) {
    element?.dispose?.();
    element?.remove();
    targetMarker?.dispose();
    reportError(options?.callbacks, error);
    throw error;
  }

  notify(state.callbacks, 'onReady', { instance: api });
  return api;
}
